/**
 * SiteLocationBoundaryEditor — edit an existing site's location (re-geocode
 * to a new address) and/or redraw / re-import its boundary, from the
 * SiteLandingPage. Two modes:
 *
 *   - `'location'` — a Places-autocomplete search input + a small preview
 *     map showing the picked point (and the existing boundary overlaid for
 *     context). On confirm, writes `location` / `address` / `siteCentroid`
 *     and — because the collected SED is now keyed to the wrong place —
 *     surfaces a non-destructive stale-evidence prompt back to the host
 *     via `onLocationChanged` (the SED is NOT wiped).
 *
 *   - `'boundary'` — reuses <SiteBoundaryDrawer> seeded with the site's
 *     existing boundary + centroid, with an "import a KMZ instead" option
 *     (parseKmzOrKml → BoundaryResult, elevation sampled via the shared
 *     helper; KMZ-borne layers are wired to updateSiteLayers). On confirm,
 *     refreshes `siteBoundary` / `siteArea` / `sitePerimeter` /
 *     `siteCentroid` / `siteElevationRange`. Boundary-only edits don't
 *     invalidate the point-keyed SED so no stale prompt fires for those.
 *
 * The editor opens to the mode the entry point requested, but the user can
 * switch modes within one session via the segmented control at the top.
 *
 * Modal shell mirrors DeleteSiteDialog's pattern (fixed overlay + centred
 * panel). Maps require the Google Maps SDK; the editor relies on the
 * <APIProvider> that already wraps SiteRagEngine (which owns the
 * SiteLandingPage that mounts this), the same way SiteBoundaryDrawer does.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Map, AdvancedMarker, Pin, useApiIsLoaded } from '@vis.gl/react-google-maps';
import { MapPin, X, Search, Check, AlertTriangle, Loader2, Pencil, Upload } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '../ui/button';
import { SiteBoundaryDrawer, type BoundaryResult } from './SiteBoundaryDrawer';
import { MultiParcelDrawer } from './MultiParcelDrawer';
import { useSiteAddressLabelSave } from './AddressLabelOverrideControl';
import { SiteBoundaryOverlay } from './SiteMapLayers';
import { parsedToSiteLayer } from './LayerReviewPanel';
// DC-3 — KMZ-import path joins the boundary-confirm cascade. Routes
// through the EAS 4-stage sampler instead of the single-shot
// `fetchPolygonElevations` so re-imported KMZ polygons get the same
// extra coverage (in-polygon grid → buffered-bbox grid → USGS
// centroid) the on-demand `refreshSiteElevation` path already has.
// Persists `cascadeStages` onto BoundaryResult so the PRE chip
// tooltip can surface the strategies the platform tried.
import { fetchPolygonElevationsCascading } from '../../lib/fetchPolygonElevationsCascading';
import { materializeSiteBoundaries, makeParcel } from '../../lib/siteBoundaryGeometry';
import type { SiteBoundaryParcel } from '../../types/siteBoundary';
import { useStore } from '../../store';
import type { SiteEntity, SiteLayer } from '../../types/site';
import { ResponsiveTabStrip } from '../ui/responsive-tab-strip';
import { SiteStudyAreaEditor } from './SiteStudyAreaEditor';
import { repairDispositionReason } from '../../lib/sim/canvas/repairDisposition';
import {
  resolveBoundaryStudyPolygons,
  resolveBoundaryEditorViewportPoints,
  detectStaleOrMissingPinWarning,
  buildInitialParcelsForMultiParcelSwitch,
  canSwitchToMultiParcelMode,
} from '../../lib/boundaryAlignmentTools';
import { evaluateStudyPolygon } from '../../lib/siteStudyArea';
import type { SiteStudyArea, StudyGeometryRef, StudyGeometryRole } from '../../types/siteStudyArea';

export type SiteLocationBoundaryMode = 'location' | 'boundary' | 'study';

/**
 * KMU — the inner Boundary view is split between the polygon-drawer
 * surface (`'draw'`) and the KMZ-import dropzone (`'import'`). Callers
 * that want to land the user straight on Import (the new split-menu
 * "Update / upload new KMZ…" affordance) pass `initialBoundaryView`;
 * everyone else gets the today's-behaviour default of `'draw'`.
 */
export type SiteLocationBoundaryView = 'draw' | 'import';

interface Props {
  site: SiteEntity;
  /** Which mode to open in. The user can switch modes inside the session. */
  initialMode: SiteLocationBoundaryMode;
  /** KMU — when initialMode === 'boundary', pre-select the inner tab.
   *  Optional; defaults to 'draw'. Ignored when initialMode === 'location'.
   *  Lets callers deep-link directly to the KMZ dropzone instead of the
   *  draw view. */
  initialBoundaryView?: SiteLocationBoundaryView;
  open: boolean;
  onClose: () => void;
  /**
   * Fired after the site's coordinates change (NOT for boundary-only edits).
   * The host (SiteLandingPage) uses this to surface the stale-evidence
   * banner + re-collect prompt. Receives the new formatted address.
   */
  onLocationChanged?: (newAddress: string) => void;
}

const API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';

/** Guard the wizard uses (SiteRagEngine ~548) — reject the null-island fallback. */
function isUsableCoord(lat: number, lng: number): boolean {
  if (!Number.isFinite(lat) || lat < -90 || lat > 90) return false;
  if (!Number.isFinite(lng) || lng < -180 || lng > 180) return false;
  if (Math.abs(lat) < 0.01 && Math.abs(lng) < 0.01) return false; // (0,0)
  return true;
}

/** Sum of haversine distances around a closed ring, in km. Mirrors the
 *  wizard's ringPerimeterKm — KMZ candidates ship area but not perimeter. */
function ringPerimeterKm(coords: { lat: number; lng: number }[]): number {
  if (coords.length < 2) return 0;
  const R = 6371; // mean Earth radius, km
  let total = 0;
  for (let i = 0; i < coords.length - 1; i++) {
    const a = coords[i];
    const b = coords[i + 1];
    const dLat = ((b.lat - a.lat) * Math.PI) / 180;
    const dLng = ((b.lng - a.lng) * Math.PI) / 180;
    const lat1 = (a.lat * Math.PI) / 180;
    const lat2 = (b.lat * Math.PI) / 180;
    const x = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
    total += 2 * R * Math.asin(Math.sqrt(x));
  }
  return parseFloat(total.toFixed(2));
}

export const SiteLocationBoundaryEditor: React.FC<Props> = ({
  site,
  initialMode,
  initialBoundaryView,
  open,
  onClose,
  onLocationChanged,
}) => {
  const updateSite = useStore(s => s.updateSite);
  const updateSiteLayers = useStore(s => s.updateSiteLayers);
  const updateSiteLayerCanvasLinks = useStore(s => s.updateSiteLayerCanvasLinks);
  const commitGeneratedNodes = useStore(s => s.commitGeneratedNodes);
  const activeRepairReason = useStore((state) => repairDispositionReason(
    state.tabs.find((tab) => tab.id === state.activeTabId),
  ));

  const [mode, setMode] = useState<SiteLocationBoundaryMode>(initialMode);
  const [saving, setSaving] = useState(false);
  const [savedBoundarySite, setSavedBoundarySite] = useState<SiteEntity | null>(null);
  // Feature #95 — "Send pipelines/areas to sim canvas" in-flight guard.
  const [sendingToSim, setSendingToSim] = useState(false);

  // Feature #95 — convert KMZ pipeline/area layers into sim canvas
  // components (pipe nodes + boundary inputs, intelligent BOP placement),
  // fetch endpoint elevations, commit to the canvas, and stamp the
  // bidirectional SiteLayer.linkedCanvasNodeId. Degrades to length-only
  // when no Maps key / outside USGS coverage.
  const pipelineLayerCount = (site.siteLayers ?? []).filter(
    l => l.category === 'infrastructure.pipeline' && l.geometry?.type === 'line',
  ).length;

  const handleSendToSimCanvas = async () => {
    // Pin the initiating canvas and its node snapshot before the first await
    // (including the lazy module import). KMZ elevation lookup can take long
    // enough for the user to switch tabs, so committing to whichever canvas is
    // active later would corrupt an unrelated model.
    const state = useStore.getState();
    const initiatingActiveTabId = state.activeTabId;
    const initiatingTab = state.tabs.find((tab) => tab.id === initiatingActiveTabId);
    if (!initiatingActiveTabId || !initiatingTab) {
      toast.error('No active simulation canvas is available for KMZ equipment.');
      return;
    }
    const liveRepairReason = repairDispositionReason(
      initiatingTab,
    );
    if (liveRepairReason) {
      toast.error('The active canvas is a read-only repair source. Create a repair copy before sending KMZ equipment.');
      return;
    }
    const layers = site.siteLayers ?? [];
    if (pipelineLayerCount === 0) {
      toast.info('No KMZ pipeline layers to send to the sim canvas.');
      return;
    }
    const initiatingNodes = [...state.nodes];
    const initiatingZoom = state.site?.zoom;
    const assertTargetReadyForCommit = () => {
      const liveState = useStore.getState();
      if (liveState.activeTabId !== initiatingActiveTabId) {
        throw new Error(
          'KMZ send cancelled because the active simulation canvas changed.',
        );
      }
      const liveTab = liveState.tabs.find((tab) => tab.id === initiatingActiveTabId);
      if (!liveTab) {
        throw new Error(
          'KMZ send cancelled because the initiating simulation canvas no longer exists.',
        );
      }
      if (repairDispositionReason(liveTab)) {
        throw new Error(
          'KMZ send cancelled because the initiating canvas is now a read-only repair source. Create a repair copy first.',
        );
      }
    };
    setSendingToSim(true);
    try {
      const { sendLayersToSimCanvas } = await import('../../lib/sim/canvas/sendLayersToSimCanvas');
      const res = await sendLayersToSimCanvas(layers, {
        assertTargetReadyForCommit,
        commitGeneratedNodes,
        existingNodes: initiatingNodes,
        persistLayerLinks: (patches) => updateSiteLayerCanvasLinks(site.id, patches),
        siteLocation: site.location,
        zoom: initiatingZoom,
      });
      const warn = res.conversion.warnings.length;
      const rec = res.conversion.recommendations.length;
      toast.success(
        `Sent ${res.committedNodeIds.length} component(s) to the sim canvas` +
          (res.elevationResolved ? ' with elevations.' : ' (length only — no elevations).'),
      );
      if (rec > 0) {
        toast.message(
          `${rec} component placement${rec === 1 ? '' : 's'} recommended (no power-gen/BOP area marked). Review before running.`,
        );
      }
      // Placement recommendations must not hide EO fluid/parameter blockers.
      // `fromKmz` orders the explicit-fluid review warning first.
      if (warn > 0) {
        toast.warning(res.conversion.warnings[0]);
      }
    } catch (e) {
      toast.error(`Failed to send layers to sim canvas: ${(e as Error)?.message ?? 'unknown error'}`);
    } finally {
      setSendingToSim(false);
    }
  };

  // Reset transient state whenever the editor (re)opens or the requested
  // entry-point mode changes.
  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setSaving(false);
    setSavedBoundarySite(null);
  }, [open, initialMode]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60"
      onClick={saving ? undefined : onClose}
      data-testid="site-location-boundary-editor"
    >
      <div
        className={`bg-background w-[720px] max-w-[95vw] max-h-[92vh] shadow-2xl border border-border rounded-2xl ${mode === 'study' ? 'flex flex-col overflow-hidden' : 'overflow-y-auto'}`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start gap-3">
          <div className="w-9 h-9 border border-border bg-state-hover flex items-center justify-center shrink-0 rounded-lg">
            {mode === 'location' ? <MapPin className="w-4 h-4 text-text-secondary" /> : <Pencil className="w-4 h-4 text-text-secondary" />}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold uppercase tracking-widest text-text-primary truncate">
              {mode === 'location' ? 'Edit site location' : mode === 'study' ? 'Define study area' : 'Edit site boundary'}
            </h2>
            <p className="text-[10px] text-text-secondary mt-0.5 truncate">
              {site.name} · {site.address}
            </p>
          </div>
          {pipelineLayerCount > 0 && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="send-to-sim-canvas"
              disabled={sendingToSim || saving || activeRepairReason !== null}
              onClick={handleSendToSimCanvas}
              className="rounded-md type-meta uppercase tracking-wider h-7 shrink-0"
              title={activeRepairReason
                ? `${activeRepairReason} Create a repair copy before sending KMZ equipment.`
                : 'Convert KMZ pipelines/areas into sim canvas components'}
            >
              {sendingToSim ? (
                <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Sending…</>
              ) : (
                <>Send to sim canvas</>
              )}
            </Button>
          )}
          <button onClick={onClose} disabled={saving} className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30" aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        {pipelineLayerCount > 0 && activeRepairReason && (
          <div
            className="mx-5 mt-4 flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 p-2.5 type-body text-warning"
            data-testid="send-to-sim-repair-guard"
          >
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>
              The active simulation is a read-only repair source. Create a repair copy in the
              Simulation editor before sending KMZ equipment to the canvas.
            </span>
          </div>
        )}

        {/* Mode switch — both modes are reachable in one session regardless
            of which entry point opened the editor. */}
        <div className="px-5 pt-4">
          <ResponsiveTabStrip
            variant="chip"
            className="w-full"
            navClassName="flex w-full [&_button]:flex-1 [&_button]:justify-center"
            ariaLabel="Editor mode"
            tabs={[
              { id: 'location', label: 'Location', icon: MapPin, testId: 'editor-mode-location', disabled: saving },
              { id: 'boundary', label: 'Boundary', icon: Pencil, testId: 'editor-mode-boundary', disabled: saving },
              { id: 'study', label: 'Study area', icon: Check, testId: 'editor-mode-study', disabled: saving },
            ]}
            activeId={mode}
            onSelect={id => {
              if (!saving) setMode(id as SiteLocationBoundaryMode);
            }}
          />
        </div>

        {/* Body */}
        <div className={mode === 'study' ? 'flex min-h-0 flex-col overflow-hidden p-5' : 'p-5'}>
          {mode === 'study' ? <SiteStudyAreaEditor site={savedBoundarySite ?? site} onClose={onClose} onSavingChange={setSaving} /> : mode === 'location' ? (
            <LocationModeBody
              site={site}
              saving={saving}
              onCancel={onClose}
              onConfirm={async ({ lat, lng, address }) => {
                if (!isUsableCoord(lat, lng)) {
                  toast.error('Invalid coordinates — pick a specific address from the search results.');
                  return;
                }
                setSaving(true);
                try {
                  // Recentre the site on the new point. If a boundary
                  // already exists keep it, but the centroid still moves
                  // to the new geocode (the user can redraw the boundary
                  // separately if it's now in the wrong place — switching
                  // to the Boundary tab is one click).
                  await updateSite(site.id, {
                    location: { lat, lng },
                    address,
                    siteCentroid: site.siteBoundary && site.siteBoundary.length >= 3
                      ? centroidOf(site.siteBoundary)
                      : { lat, lng },
                  });
                  toast.success(`Site location updated to ${address}`);
                  onLocationChanged?.(address);
                  onClose();
                } catch (e) {
                  toast.error(`Failed to update location: ${(e as Error)?.message ?? 'unknown error'}`);
                  setSaving(false);
                }
              }}
            />
          ) : (
            <BoundaryModeBody
              site={site}
              saving={saving}
              initialBoundaryView={initialBoundaryView ?? 'draw'}
              onCancel={onClose}
              onConfirm={async (result, importedLayers, stagedCommit) => {
                setSaving(true);
                try {
                  const storeState = useStore.getState() as unknown as {
                    sites?: SiteEntity[];
                    siteRag?: { sites?: SiteEntity[] };
                  };
                  const storeSites = storeState.sites ?? storeState.siteRag?.sites ?? [];
                  const latestSite = storeSites.find((s: SiteEntity) => s.id === site.id) ?? site;
                  if (
                    stagedCommit?.studyAreaChanged &&
                    (latestSite.studyArea?.revision ?? 0) !== (site.studyArea?.revision ?? 0)
                  ) {
                    throw new Error(
                      'The study selection changed while this editor was open. Reopen it to review the latest selection.',
                    );
                  }
                  const initialBoundaryKey = JSON.stringify({
                    siteBoundaries: site.siteBoundaries ?? null,
                    siteBoundary: site.siteBoundary ?? null,
                  });
                  const latestBoundaryKey = JSON.stringify({
                    siteBoundaries: latestSite.siteBoundaries ?? null,
                    siteBoundary: latestSite.siteBoundary ?? null,
                  });
                  if (latestBoundaryKey !== initialBoundaryKey) {
                    throw new Error(
                      'The site boundary changed while this editor was open. Reopen it to review the latest boundary.',
                    );
                  }

                  // Address-Coordinate Reconciliation AC2 — when the user
                  // redraws or re-imports the boundary the address must
                  // follow the new centroid. Reverse-geocode FIRST so we
                  // can stamp the new address in the same updateSite call
                  // (one Firestore write). On failure, keep the existing
                  // address and don't stamp provenance — the chip stays
                  // hidden and the boundary still wins for data calls.
                  const { reverseGeocodeLatLng } = await import('../../services/geocoding');
                  const rev = await reverseGeocodeLatLng(result.centroid.lat, result.centroid.lng);
                  const addressPatch = rev
                    ? {
                        address: rev.formattedAddress,
                        geocodingProvenance: 'reverse-geocode' as const,
                        addressUpdatedAt: new Date().toISOString(),
                      }
                    : {};
                  // EL2: BoundaryResult elevation fields are `number | null`.
                  // Persist siteElevationRange ONLY when all three are finite;
                  // otherwise leave undefined (the field is optional on Site).
                  // Units: metres ASL.
                  const eMin = result.elevationMin;
                  const eMax = result.elevationMax;
                  const eAvg = result.elevationAvg;
                  const allElevFinite =
                    typeof eMin === 'number' && Number.isFinite(eMin) &&
                    typeof eMax === 'number' && Number.isFinite(eMax) &&
                    typeof eAvg === 'number' && Number.isFinite(eAvg);
                  // MP2 — every save persists the canonical
                  // multi-parcel shape. When `result.parcels` is set
                  // (N-polygon path), use it; otherwise lift the single
                  // polygon into a one-parcel canonical record so the
                  // siteBoundaries[] / siteBoundaryAggregate fields are
                  // always present going forward. Back-compat aliases
                  // (siteBoundary / siteArea / sitePerimeter /
                  // siteCentroid) below stay equal to the first-parcel
                  // / aggregate derivatives so legacy readers keep
                  // working.
                  const { makeParcel, computeAggregate } = await import('../../lib/siteBoundaryGeometry');
                  const canonicalParcels = result.parcels && result.parcels.length > 0
                    ? result.parcels
                    : [
                        makeParcel(result.boundary, {
                          id: materializeSiteBoundaries(latestSite)[0]?.id ?? 'p1',
                          label: materializeSiteBoundaries(latestSite)[0]?.label ?? 'Site',
                          area: result.areaAcres,
                          perimeter: result.perimeterKm,
                          centroid: result.centroid,
                        }),
                      ];
                  const aggregate = computeAggregate(canonicalParcels);
                  const studyArea = stagedCommit?.studyAreaChanged && stagedCommit.nextStudyArea
                    ? stagedCommit.nextStudyArea
                    : (latestSite.studyArea ?? site.studyArea ?? {
                        schemaVersion: 1 as const, name: 'DC + BTM development area', revision: 1,
                        included: [], roles: [], updatedAt: new Date().toISOString(),
                      });
                  await updateSite(site.id, {
                    studyArea,
                    siteBoundaries: canonicalParcels,
                    siteBoundaryAggregate: aggregate,
                    // MP1 back-compat aliases — derived from index 0
                    // (single-parcel) or aggregate (multi-parcel) so
                    // existing readers (DOCX export, kmzExporter, every
                    // per-type renderer that hasn't migrated yet)
                    // continue to read these.
                    siteBoundary: canonicalParcels[0].polygon,
                    siteArea: aggregate.totalArea,
                    sitePerimeter: aggregate.totalPerimeter,
                    siteCentroid: aggregate.aggregateCentroid,
                    // MP11 — flag the canonical shape as persisted so
                    // the lazy migration doesn't re-audit-log on read.
                    boundary_migrated_to_multi_parcel: true,
                    // `location` is the geocoded-address point; for legacy
                    // sites with no centroid it was the only spatial field.
                    // Boundary-led sites no longer depend on it, but we
                    // refresh it to the aggregate centroid so providers
                    // that still consult `location` (very few) stay
                    // aligned.
                    location: aggregate.aggregateCentroid,
                    siteElevationRange: allElevFinite
                      ? {
                          min: eMin,
                          max: eMax,
                          avg: eAvg,
                          // EL3 — thread stdev + pointsSampled.
                          stdev: result.elevationStdev ?? null,
                          pointsSampled: result.elevationPointsSampled,
                          // DC-3 — persist the cascade audit trail
                          // onto the site so the SED bootstrap can
                          // stamp `attemptStages` on the siteProfile
                          // section (same channel refreshSiteElevation
                          // uses). Both fields are optional + back-
                          // compat — legacy reads ignore them.
                          ...(result?.sourceQualification ? { sourceQualification: result.sourceQualification } : {}),
                          ...(result.cascadeSource ? { cascadeSource: result.cascadeSource } : {}),
                          ...(result.cascadeStages && result.cascadeStages.length > 0
                            ? { cascadeStages: result.cascadeStages }
                            : {}),
                        }
                      : undefined,
                    ...addressPatch,
                  });
                  // KSD8 — per-layer dedup + staged visibility merge.
                  // Apply staged visibility changes onto the latest store
                  // `siteLayers` (preserving unrelated layers), and merge
                  // freshly-imported KMZ layers if present.
                  const latestBaseLayers = (latestSite.siteLayers ?? site.siteLayers ?? []).map((layer: SiteLayer) => {
                    if (
                      stagedCommit?.visibilityChanged &&
                      Object.prototype.hasOwnProperty.call(stagedCommit.visibilityById, layer.id)
                    ) {
                      return { ...layer, visible: stagedCommit.visibilityById[layer.id]! };
                    }
                    return layer;
                  });
                  let mergedLayers: SiteLayer[] | undefined = stagedCommit?.visibilityChanged
                    ? latestBaseLayers
                    : importedLayers;
                  let dedupSummary: string | null = null;
                  if (importedLayers && importedLayers.length > 0) {
                    const { mergeImportedLayers } = await import('../../services/kmzParser');
                    const merge = mergeImportedLayers(latestBaseLayers, importedLayers);
                    mergedLayers = merge.merged;
                    if (merge.skipped.length > 0) {
                      dedupSummary = `${merge.skipped.length} of ${importedLayers.length} layers were duplicates of existing layers; skipping. ${merge.added.length} new layer${merge.added.length === 1 ? '' : 's'} added.`;
                    }
                    await updateSiteLayers(site.id, mergedLayers);
                  } else if (stagedCommit?.visibilityChanged) {
                    await updateSiteLayers(site.id, latestBaseLayers);
                  }
                  const elevPart = allElevFinite
                    ? `elevation ${eMin}–${eMax}m`
                    : 'elevation unavailable';
                  // MP2/MP3 — multi-parcel sites report the aggregate
                  // total + parcel count so the success toast is
                  // truthful even when the user added a second parcel.
                  const acrePart = canonicalParcels.length > 1
                    ? `${Math.round(aggregate.totalArea)} ac across ${canonicalParcels.length} parcels${aggregate.isDisjoint ? ' (disjoint)' : ''}`
                    : `${Math.round(result.areaAcres)} acres`;
                  toast.success(
                    `Site boundary updated — ${acrePart}, ${elevPart}${
                      importedLayers && importedLayers.length > 0 ? `, ${importedLayers.length} layer${importedLayers.length === 1 ? '' : 's'} imported` : ''
                    }`,
                  );
                  if (dedupSummary) toast.message(dedupSummary);
                  if (rev) {
                    toast.message(
                      `Address updated to "${rev.locality ?? rev.formattedAddress}" — derived from the new boundary centroid.`,
                    );
                  }
                  setSavedBoundarySite({
                    ...latestSite, siteBoundaries: canonicalParcels, siteBoundaryAggregate: aggregate,
                    siteBoundary: canonicalParcels[0].polygon, siteArea: aggregate.totalArea,
                    sitePerimeter: aggregate.totalPerimeter, siteCentroid: aggregate.aggregateCentroid,
                    siteLayers: mergedLayers ?? latestSite.siteLayers ?? site.siteLayers, studyArea,
                  });
                  setSaving(false);
                  setMode('study');
                } catch (e) {
                  toast.error(`Failed to update boundary: ${(e as Error)?.message ?? 'unknown error'}`);
                  setSaving(false);
                }
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Location mode ───────────────────────────────────────────────────── */

interface PickedPlace {
  lat: number;
  lng: number;
  address: string;
  name?: string;
}

function LocationModeBody({
  site,
  saving,
  onCancel,
  onConfirm,
}: {
  site: SiteEntity;
  saving: boolean;
  onCancel: () => void;
  onConfirm: (p: { lat: number; lng: number; address: string }) => void;
}) {
  const isApiLoaded = useApiIsLoaded();
  const [inputEl, setInputEl] = useState<HTMLInputElement | null>(null);
  const [picked, setPicked] = useState<PickedPlace | null>(null);
  const [query, setQuery] = useState('');

  // Reuse the wizard's Places Autocomplete wiring (SiteRagEngine ~733).
  // TODO(maps-migration): Replace Autocomplete with PlaceAutocompleteElement.
  useEffect(() => {
    if (!inputEl || !isApiLoaded) return;
    const autocomplete = new google.maps.places.Autocomplete(inputEl, {
      fields: ['geometry', 'name', 'formatted_address'],
    });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      const loc = place.geometry?.location;
      if (!loc) {
        toast.error('Could not resolve that place — pick a result from the dropdown.');
        return;
      }
      const lat = loc.lat();
      const lng = loc.lng();
      const address = place.formatted_address || place.name || 'Unknown Address';
      setPicked({ lat, lng, address, name: place.name ?? undefined });
      setQuery(place.name || place.formatted_address || '');
    });
    return () => google.maps.event.clearInstanceListeners(autocomplete);
  }, [inputEl, isApiLoaded]);

  const previewCenter = picked
    ? { lat: picked.lat, lng: picked.lng }
    : (site.siteCentroid ?? site.location);
  const moved = picked && (picked.lat !== site.location.lat || picked.lng !== site.location.lng);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <label className="type-section text-text-secondary">
          New address
        </label>
        <div className="relative">
          <Search className="w-3.5 h-3.5 text-text-muted absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            ref={setInputEl}
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            disabled={saving || !isApiLoaded}
            placeholder={isApiLoaded ? 'Search for an address, e.g. "Sicklerville, Winslow Township, NJ 08081, USA"' : 'Loading maps…'}
            className="w-full text-[11px] pl-8 pr-3 py-2 border border-border rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 font-mono disabled:opacity-50"
            data-testid="location-search-input"
          />
        </div>
        <p className="type-body text-text-muted uppercase tracking-widest">
          Current: {site.address} ({site.location.lat.toFixed(5)}, {site.location.lng.toFixed(5)})
        </p>
      </div>

      {/* Preview map — picked point + existing boundary for context. */}
      <div className="h-[40vh] min-h-[260px] border border-border rounded-lg overflow-hidden relative">
        {API_KEY ? (
          <Map
            mapId="site-location-editor-map"
            // `key` forces a recentre when the user picks a new place — the
            // SDK only reads defaultCenter once.
            key={`${previewCenter.lat},${previewCenter.lng}`}
            defaultCenter={previewCenter}
            defaultZoom={picked ? 15 : 13}
            mapTypeId="hybrid"
            gestureHandling="cooperative"
            streetViewControl={false}
          >
            <AdvancedMarker position={previewCenter}>
              <Pin background="#D97706" glyphColor="#fff" borderColor="#92400e" />
            </AdvancedMarker>
            {site.siteBoundary && site.siteBoundary.length >= 3 && (
              <SiteBoundaryOverlay path={site.siteBoundary} strokeColor="#94a3b8" fillOpacity={0.08} />
            )}
          </Map>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-[10px] text-text-muted uppercase tracking-widest">
            Maps API key not configured
          </div>
        )}
      </div>

      {picked && (
        <div className="p-3 border border-success/40 bg-success/10 rounded-md space-y-0.5">
          <div className="text-[10px] font-bold text-success uppercase tracking-widest">{picked.name ?? 'Selected location'}</div>
          <div className="text-[11px] text-text-muted">{picked.address}</div>
          <div className="type-body text-text-muted font-mono">{picked.lat.toFixed(5)}, {picked.lng.toFixed(5)}</div>
        </div>
      )}

      {site.siteBoundary && site.siteBoundary.length >= 3 && moved && (
        <div className="flex items-start gap-2 p-2.5 border border-warning/40 bg-warning/10 rounded-md type-body text-warning uppercase tracking-widest">
          <AlertTriangle className="w-3 h-3 shrink-0 mt-px" />
          <span>This site already has a drawn boundary. The boundary will be kept as-is — switch to the Boundary tab afterwards if it needs redrawing for the new location.</span>
        </div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <Button variant="outline" size="sm" className="h-8 rounded-md type-meta" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <div className="flex-1" />
        <Button
          variant="default"
          size="sm"
          className="h-8 rounded-md type-meta"
          disabled={!picked || saving}
          onClick={() => picked && onConfirm({ lat: picked.lat, lng: picked.lng, address: picked.address })}
          data-testid="location-confirm"
        >
          {saving ? <Loader2 className="w-3 h-3 mr-2 animate-spin" /> : <Check className="w-3 h-3 mr-2" />}
          Update location
        </Button>
      </div>
    </div>
  );
}

/* ── Boundary mode ───────────────────────────────────────────────────── */

interface StagedBoundaryLinkageCommit {
  visibilityChanged: boolean;
  visibilityById: Record<string, boolean>;
  studyAreaChanged: boolean;
  nextStudyArea: SiteStudyArea | null;
}

function BoundaryModeBody({
  site,
  saving,
  initialBoundaryView,
  onCancel,
  onConfirm,
}: {
  site: SiteEntity;
  saving: boolean;
  /** KMU — seeds the inner draw/import tab. Defaults to 'draw' when the
   *  parent omits it (back-compat with every legacy caller). */
  initialBoundaryView: SiteLocationBoundaryView;
  onCancel: () => void;
  onConfirm: (
    result: BoundaryResult,
    importedLayers?: SiteLayer[],
    stagedCommit?: StagedBoundaryLinkageCommit,
  ) => void;
}) {
  const updateSite = useStore(s => s.updateSite);
  const onSaveLabel = useSiteAddressLabelSave(site, updateSite);
  const [drawMode, setDrawMode] = useState<SiteLocationBoundaryView>(initialBoundaryView);
  const [isImportingKmz, setIsImportingKmz] = useState(false);
  const [forceMultiParcelMode, setForceMultiParcelMode] = useState(false);
  const [singleDraftParcels, setSingleDraftParcels] = useState<SiteBoundaryParcel[] | null>(null);
  const [pendingImport, setPendingImport] = useState<{ result: BoundaryResult; layers: SiteLayer[]; fileName: string } | null>(null);
  const importEpoch = useRef(0);
  useEffect(() => () => { importEpoch.current += 1; }, []);

  // Evaluate each siteLayer's polygon geometry hash once per siteLayers change
  const layerEvaluations = useMemo(() => {
    const map = new globalThis.Map<string, { valid: boolean; geometryHash?: string; error?: string }>();
    for (const layer of site.siteLayers ?? []) {
      if (!layer || layer.geometry?.type !== 'polygon' || (layer.geometry.coordinates ?? []).length < 3) {
        map.set(layer.id, {
          valid: false,
          error: 'Only valid polygon layers can be selected for the study area.',
        });
        continue;
      }
      const evaluated = evaluateStudyPolygon(layer.geometry.coordinates, { holes: layer.geometry.holes });
      if ('geometryHash' in evaluated) {
        map.set(layer.id, { valid: true, geometryHash: evaluated.geometryHash });
      } else {
        map.set(layer.id, { valid: false, error: evaluated.error });
      }
    }
    return map;
  }, [site.siteLayers]);

  const initialVisibilityById = useMemo(() => {
    const out: Record<string, boolean> = {};
    for (const l of site.siteLayers ?? []) {
      out[l.id] = l.visible !== false;
    }
    return out;
  }, [site.siteLayers]);

  const initialIncludedLayerIds = useMemo(() => {
    const ids = new Set<string>();
    for (const ref of site.studyArea?.included ?? []) {
      if (ref.kind !== 'layer') continue;
      // Keep authoritative pins selected even when their current geometry is stale or missing.
      ids.add(ref.id);
    }
    return ids;
  }, [site.studyArea?.included, layerEvaluations]);

  const [stagedVisibilityById, setStagedVisibilityById] =
    useState<Record<string, boolean>>(initialVisibilityById);
  const [hasStagedVisibilityChanges, setHasStagedVisibilityChanges] = useState(false);
  const [stagedIncludedLayerIds, setStagedIncludedLayerIds] =
    useState<Set<string>>(initialIncludedLayerIds);
  const [hasStagedStudyAreaChanges, setHasStagedStudyAreaChanges] = useState(false);
  const [touchedStudyLayerIds, setTouchedStudyLayerIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setStagedVisibilityById(initialVisibilityById);
    setHasStagedVisibilityChanges(false);
    setStagedIncludedLayerIds(initialIncludedLayerIds);
    setHasStagedStudyAreaChanges(false);
    setTouchedStudyLayerIds(new Set());
  }, [site.id, site.studyArea?.revision]); // eslint-disable-line react-hooks/exhaustive-deps

  const stagedStudyArea = useMemo<SiteStudyArea | undefined>(() => {
    if (!hasStagedStudyAreaChanges) {
      return site.studyArea;
    }
    const existingParcelRefs = (site.studyArea?.included ?? []).filter(
      (r): r is StudyGeometryRef & { geometryHash: string } => r.kind === 'parcel',
    );
    const existingParcelRoles = (site.studyArea?.roles ?? []).filter(
      r => r.kind === 'parcel',
    );
    const existingLayerRoleById = new globalThis.Map<string, StudyGeometryRole>(
      (site.studyArea?.roles ?? [])
        .filter(r => r.kind === 'layer')
        .map(r => [r.id, r.role]),
    );
    // Preserve untouched authoritative references byte-for-byte, including unresolved pins.
    const nextLayerRefs: Array<StudyGeometryRef & { geometryHash: string }> =
      (site.studyArea?.included ?? []).filter(
        r => r.kind === 'layer' && stagedIncludedLayerIds.has(r.id) && !touchedStudyLayerIds.has(r.id),
      );
    const nextLayerRoles: Array<{ kind: 'layer'; id: string; role: StudyGeometryRole }> =
      (site.studyArea?.roles ?? []).filter(
        (r): r is { kind: 'layer'; id: string; role: StudyGeometryRole } =>
          r.kind === 'layer' && stagedIncludedLayerIds.has(r.id) && !touchedStudyLayerIds.has(r.id),
      );

    for (const layer of site.siteLayers ?? []) {
      if (!stagedIncludedLayerIds.has(layer.id) || !touchedStudyLayerIds.has(layer.id)) continue;
      const evalInfo = layerEvaluations.get(layer.id);
      if (!evalInfo?.valid || !evalInfo.geometryHash) continue;
      nextLayerRefs.push({
        kind: 'layer',
        id: layer.id,
        geometryHash: evalInfo.geometryHash,
      });
      nextLayerRoles.push({
        kind: 'layer',
        id: layer.id,
        role: existingLayerRoleById.get(layer.id) ?? 'development',
      });
    }

    return {
      schemaVersion: 1 as const,
      name: site.studyArea?.name ?? 'DC + BTM development area',
      revision: (site.studyArea?.revision ?? 0) + 1,
      updatedAt: new Date().toISOString(),
      included: [...existingParcelRefs, ...nextLayerRefs],
      roles: [...existingParcelRoles, ...nextLayerRoles],
    };
  }, [
    hasStagedStudyAreaChanges,
    site.studyArea,
    site.siteLayers,
    stagedIncludedLayerIds,
    touchedStudyLayerIds,
    layerEvaluations,
  ]);

  const stagedSite = useMemo<SiteEntity>(() => {
    const nextLayers = (site.siteLayers ?? []).map(l => ({
      ...l,
      visible: Object.prototype.hasOwnProperty.call(stagedVisibilityById, l.id)
        ? stagedVisibilityById[l.id]!
        : l.visible !== false,
    }));
    return {
      ...site,
      siteLayers: nextLayers,
      studyArea: stagedStudyArea,
    };
  }, [site, stagedVisibilityById, stagedStudyArea]);

  const stagedCommitPayload = useMemo<StagedBoundaryLinkageCommit>(
    () => ({
      visibilityChanged: hasStagedVisibilityChanges,
      visibilityById: stagedVisibilityById,
      studyAreaChanged: hasStagedStudyAreaChanges,
      nextStudyArea: hasStagedStudyAreaChanges ? (stagedStudyArea ?? null) : null,
    }),
    [
      hasStagedVisibilityChanges,
      stagedVisibilityById,
      hasStagedStudyAreaChanges,
      stagedStudyArea,
    ],
  );

  const studyAreaPolygons = useMemo(
    () => resolveBoundaryStudyPolygons(stagedSite),
    [stagedSite],
  );
  const viewportPoints = useMemo(
    () => resolveBoundaryEditorViewportPoints(stagedSite),
    [stagedSite],
  );
  const pinWarning = useMemo(
    () => detectStaleOrMissingPinWarning(stagedSite),
    [stagedSite],
  );

  const center = useMemo(() => {
    if (pinWarning.hasWarning && pinWarning.suggestedCentroid) {
      return {
        lat: pinWarning.suggestedCentroid[0],
        lng: pinWarning.suggestedCentroid[1],
      };
    }
    return site.siteCentroid ?? site.location;
  }, [pinWarning.hasWarning, pinWarning.suggestedCentroid, site.siteCentroid, site.location]);

  const handleToggleLayerVisibility = (layerId: string) => {
    setStagedVisibilityById(prev => {
      const current = Object.prototype.hasOwnProperty.call(prev, layerId)
        ? prev[layerId]!
        : true;
      return {
        ...prev,
        [layerId]: !current,
      };
    });
    setHasStagedVisibilityChanges(true);
  };

  const handleToggleLayerStudyArea = (layerId: string) => {
    const evalInfo = layerEvaluations.get(layerId);
    if (!stagedIncludedLayerIds.has(layerId) && (!evalInfo?.valid || !evalInfo.geometryHash)) {
      toast.error(evalInfo?.error ?? 'This layer cannot be selected for the study area.');
      return;
    }
    setStagedIncludedLayerIds(prev => {
      const next = new Set(prev);
      if (next.has(layerId)) {
        next.delete(layerId);
      } else {
        next.add(layerId);
      }
      return next;
    });
    setTouchedStudyLayerIds(prev => new Set([...prev, layerId]));
    setHasStagedStudyAreaChanges(true);
  };

  const handleKmzFile = async (file: File) => {
    const epoch = ++importEpoch.current;
    setPendingImport(null);
    setIsImportingKmz(true);
    try {
      const { parseKmzOrKml } = await (await import('../../utils/safeImport')).safeImport(() => import('../../services/kmzParser'));
      const parsed = await parseKmzOrKml(file);
      if (epoch !== importEpoch.current) return;
      for (const w of parsed.warnings) toast.message(w);
      if (parsed.boundaries.length === 0) {
        toast.error('No boundary polygon found in the KMZ. Try drawing instead.');
        return;
      }
      const top = parsed.boundaries[0];
      let perimeterKm = ringPerimeterKm(top.coordinates);
      // Sample elevation for the imported polygon. KMZ carries no
      // elevation, so the cascade fills it in.
      // EL2: when every cascade stage fails, propagate `null` (NOT
      // 0) so the SED-assembly + render layer can say "Data
      // unavailable" instead of synthesising "0 m ASL". Units:
      // metres ASL.
      // DC-3 — cascade returns success-with-stats OR empty-with-
      // stages; both branches carry the per-stage audit trail so the
      // PRE chip can surface "Tried 4 strategies: …".
      const cascade = await fetchPolygonElevationsCascading(top.coordinates);
      if (epoch !== importEpoch.current) return;
      const elev = cascade.kind === 'success' ? cascade.stats : null;
      const cascadeStages = cascade.kind === 'success'
        ? cascade.stats.stages
        : cascade.meta.stages;
      const cascadeSource = cascade.kind === 'success' ? cascade.stats.source : undefined;

      // MP10 / MP11 — KMZ with a MultiPolygon (or multiple Polygon
      // placemarks tagged as boundary candidates) becomes N parcels.
      // The legacy path used only `top`; we keep that for the
      // BoundaryResult.boundary / areaAcres / perimeterKm fields
      // (back-compat) AND lift every parsed candidate into a parcels[]
      // array when more than one is present. The save path in
      // SiteLocationBoundaryEditor reads result.parcels first and
      // falls back to single-polygon when absent.
      let parcels: import('../../types/siteBoundary').SiteBoundaryParcel[] | undefined;
      let areaAcres = top.areaAcres;
      let centroid = top.centroid;
      if (parsed.boundaries.length > 1 || (top.holes && top.holes.length > 0)) {
        const { makeParcel, computeAggregate, parcelPerimeter } = await import('../../lib/siteBoundaryGeometry');
        parcels = parsed.boundaries.map((cand, i) =>
          makeParcel(cand.coordinates, {
            id: `p${i + 1}`,
            label: cand.name ?? `Parcel ${i + 1}`,
            ...(cand.holes && cand.holes.length > 0 ? { holes: cand.holes } : {}),
            area: cand.areaAcres,
            perimeter: cand.holes && cand.holes.length > 0
              ? parcelPerimeter(cand.coordinates, cand.holes)
              : ringPerimeterKm(cand.coordinates),
            centroid: cand.centroid,
          }),
        );
        const aggregate = computeAggregate(parcels);
        areaAcres = Math.round(aggregate.totalArea * 100) / 100;
        perimeterKm = aggregate.totalPerimeter;
        centroid = aggregate.aggregateCentroid;
        if (parsed.boundaries.length > 1) {
          toast.message(
            `KMZ multi-parcel detected — importing ${parcels.length} site parcels (${Math.round(areaAcres)} ac total).`,
          );
        }
      }

      const result: BoundaryResult = {
        boundary: top.coordinates,
        areaAcres,
        perimeterKm,
        centroid,
        elevationMin: elev?.min ?? null,
        elevationMax: elev?.max ?? null,
        elevationAvg: elev?.avg ?? null,
        elevationStdev: elev?.stdev ?? null,
        elevationPointsSampled: elev?.pointsSampled,
        // DC-3 — thread the cascade audit trail through BoundaryResult
        // so the consumer's updateSite call can stamp it onto
        // siteElevationRange (same channel refreshSiteElevation uses).
        ...(cascadeSource ? { cascadeSource } : {}),
        ...(cascadeStages && cascadeStages.length > 0
          ? { cascadeStages }
          : {}),
        parcels,
      };
      const layers = parsed.layers.map(parsedToSiteLayer);
      // Parsing and provider lookup are preparation only; persistence requires explicit confirmation.
      if (epoch === importEpoch.current) setPendingImport({ result, layers, fileName: file.name });
    } catch (e) {
      if (epoch === importEpoch.current) toast.error(`KMZ parse failed: ${(e as Error)?.message ?? 'unknown error'}`);
    } finally {
      if (epoch === importEpoch.current) setIsImportingKmz(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-[10px] text-text-muted uppercase tracking-widest">
        Redraw the boundary on the satellite map, or import a fresh KMZ / KML from Google Earth / ArcGIS / QGIS. Confirming replaces the existing boundary, area, perimeter and elevation.
      </p>

      <div className="flex border border-border-subtle bg-background rounded-lg overflow-hidden">
        {([['draw', 'Draw / edit'], ['import', 'Import KMZ']] as Array<[SiteLocationBoundaryView, string]>).map(([m, label]) => (
          <button
            key={m}
            type="button"
            disabled={saving}
            onClick={() => setDrawMode(m)}
            data-testid={`boundary-submode-${m}`}
            className={`flex-1 px-4 py-2 type-section transition-colors disabled:opacity-50 ${
              drawMode === m ? 'selected-accent' : 'text-text-secondary hover:bg-state-hover'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {pinWarning.hasWarning && (
        <div
          data-testid="boundary-pin-warning"
          className="flex flex-wrap items-center justify-between gap-2 p-2.5 border border-warning/40 bg-warning/10 rounded-md text-xs text-warning"
        >
          <div className="flex items-start gap-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>{pinWarning.message}</span>
          </div>
        </div>
      )}

      {(site.siteLayers ?? []).length > 0 && (
        <div
          data-testid="boundary-study-area-linkage-panel"
          className="p-2.5 border border-border-subtle bg-state-hover/50 rounded-md space-y-2"
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-bold uppercase tracking-widest text-text-secondary">
              Study Area & Layer Linkage ({studyAreaPolygons.filter((p) => p.source === 'study-area-layer').length} selected in blue)
            </span>
            <span className="text-[10px] text-text-muted">
              Study-area layers render in blue (#2563eb) even when layer display is hidden · saved on Confirm
            </span>
          </div>
          <div className="flex flex-wrap gap-2 max-h-[96px] overflow-y-auto">
            {(site.siteLayers ?? []).map((layer) => {
              const isVisible = Object.prototype.hasOwnProperty.call(stagedVisibilityById, layer.id)
                ? stagedVisibilityById[layer.id]!
                : layer.visible !== false;
              const evalInfo = layerEvaluations.get(layer.id);
              const canSelectStudy = stagedIncludedLayerIds.has(layer.id) || Boolean(evalInfo?.valid && evalInfo.geometryHash);
              const isStudySelected = stagedIncludedLayerIds.has(layer.id);
              return (
                <div
                  key={layer.id}
                  data-testid={`boundary-layer-linkage-row-${layer.id}`}
                  className="inline-flex items-center gap-2 px-2 py-1 border border-border-subtle bg-background rounded text-[11px]"
                >
                  <span className="font-medium text-text-primary truncate max-w-[160px]">
                    {layer.name || layer.id}
                  </span>
                  <label className="inline-flex items-center gap-1 text-[10px] text-text-secondary cursor-pointer">
                    <input
                      type="checkbox"
                      checked={isVisible}
                      disabled={saving}
                      onChange={() => handleToggleLayerVisibility(layer.id)}
                      data-testid={`boundary-layer-visible-toggle-${layer.id}`}
                    />
                    Visible
                  </label>
                  <label
                    className={`inline-flex items-center gap-1 text-[10px] font-semibold ${
                      canSelectStudy ? 'text-primary cursor-pointer' : 'text-text-muted opacity-60 cursor-not-allowed'
                    }`}
                    title={canSelectStudy ? undefined : evalInfo?.error}
                  >
                    <input
                      type="checkbox"
                      checked={isStudySelected}
                      disabled={!canSelectStudy || saving}
                      onChange={() => handleToggleLayerStudyArea(layer.id)}
                      data-testid={`boundary-layer-study-toggle-${layer.id}`}
                    />
                    Study Area (Blue)
                  </label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {drawMode === 'draw' ? (
        (() => {
          const existingParcels = materializeSiteBoundaries({
            siteBoundaries: site.siteBoundaries,
            siteBoundary: site.siteBoundary,
            siteArea: site.siteArea,
            sitePerimeter: site.sitePerimeter,
            siteCentroid: site.siteCentroid,
          });
          const useMultiParcel = existingParcels.length >= 2 || existingParcels.some(p => p.holes?.length) || forceMultiParcelMode;
          const initialParcelsForDrawer = singleDraftParcels ??
            (existingParcels.length >= 2 || existingParcels.some(p => p.holes?.length)
              ? existingParcels
              : buildInitialParcelsForMultiParcelSwitch(stagedSite));
          if (useMultiParcel) {
            return (
              <MultiParcelDrawer
                center={center}
                siteId={site.id}
                initialParcels={initialParcelsForDrawer}
                existingLayers={(stagedSite.siteLayers ?? []).filter(l => l.visible !== false)}
                studyAreaPolygons={studyAreaPolygons}
                viewportExtraPoints={viewportPoints}
                apiKey={API_KEY}
                addressLabel={site.addressLabel}
                canonicalAddress={site.address ?? ''}
                onSaveLabel={onSaveLabel}
                onConfirm={result => onConfirm(result, undefined, stagedCommitPayload)}
                onSkip={onCancel}
              />
            );
          }
          return (
            <SiteBoundaryDrawer
              center={center}
              existingBoundary={site.siteBoundary && site.siteBoundary.length >= 3 ? site.siteBoundary : undefined}
              existingLayers={(stagedSite.siteLayers ?? []).filter(l => l.visible !== false)}
              studyAreaPolygons={studyAreaPolygons}
              viewportExtraPoints={viewportPoints}
              onSwitchToMultiParcel={
                canSwitchToMultiParcelMode(stagedSite)
                  ? (draft) => {
                      const prior = materializeSiteBoundaries(site)[0];
                      setSingleDraftParcels(draft ? [makeParcel(draft.boundary, {
                        ...prior,
                        id: prior?.id ?? 'p1', label: prior?.label ?? 'Site',
                        area: draft.areaAcres, perimeter: draft.perimeterKm, centroid: draft.centroid,
                      })] : []);
                      setForceMultiParcelMode(true);
                    }
                  : undefined
              }
              apiKey={API_KEY}
              addressLabel={site.addressLabel}
              canonicalAddress={site.address ?? ''}
              onSaveLabel={onSaveLabel}
              onConfirm={result => onConfirm(result, undefined, stagedCommitPayload)}
              onSkip={onCancel}
            />
          );
        })()
      ) : (
        <div className="space-y-3">
          {pendingImport && (
            <div data-testid="boundary-import-preview" className="rounded-md border border-border p-3 space-y-2">
              <p>{pendingImport.fileName}: {pendingImport.result.parcels?.length ?? 1} parcel(s), {Math.round(pendingImport.result.areaAcres)} acres. Confirm to replace the saved boundary.</p>
              <Button disabled={saving || isImportingKmz} data-testid="boundary-import-confirm" onClick={() => onConfirm(pendingImport.result, pendingImport.layers.length ? pendingImport.layers : undefined, stagedCommitPayload)}>Confirm imported boundary</Button>
              <Button variant="outline" disabled={saving} onClick={() => { importEpoch.current += 1; setPendingImport(null); }}>Discard import</Button>
            </div>
          )}
          <label className="block">
            <input
              type="file"
              accept=".kmz,.kml"
              className="hidden"
              disabled={isImportingKmz || saving}
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) void handleKmzFile(f);
                e.target.value = '';
              }}
            />
            <div className={`border-2 border-dashed border-border rounded-xl p-8 text-center cursor-pointer transition-colors ${isImportingKmz || saving ? 'opacity-50 pointer-events-none' : 'hover:bg-state-hover hover:border-primary/30'}`}>
              {isImportingKmz ? (
                <div className="space-y-2">
                  <Loader2 className="w-6 h-6 animate-spin text-text-muted mx-auto" />
                  <span className="text-[10px] text-text-muted uppercase tracking-widest block font-bold">Parsing KMZ…</span>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="w-6 h-6 text-text-disabled mx-auto" />
                  <span className="text-[10px] text-text-muted uppercase tracking-widest block font-bold">Drop a .kmz / .kml or click to upload</span>
                  <span className="text-xs text-text-disabled block">Boundary replaces the current one; layers are added to the site</span>
                </div>
              )}
            </div>
          </label>
          <div className="flex">
            <Button variant="outline" size="sm" className="h-8 rounded-md type-meta" onClick={onCancel} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Helpers ─────────────────────────────────────────────────────────── */

function centroidOf(ring: { lat: number; lng: number }[]): { lat: number; lng: number } {
  return {
    lat: ring.reduce((s, p) => s + p.lat, 0) / ring.length,
    lng: ring.reduce((s, p) => s + p.lng, 0) / ring.length,
  };
}
